import React, { Dispatch, FC, ReactNode, SetStateAction, useMemo } from 'react';

import { styled } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { QuizColorsMode } from '~/blocks/TestCard/TestCard';
import Question from '~/components/QuestionQuiz/Question';
import QuestionExplainer from '~/components/QuestionQuiz/QuestionExplainer';
import QuestionTitle from '~/components/QuestionQuiz/QuestionTitle';
import ReportForm from '~/components/QuestionQuiz/ReportForm';
import Slides, { ISlidesActions } from '~/components/QuestionQuiz/Slides';
import createCorrectAnswersByIdMap from '~/components/QuestionQuiz/createCorrectAnswersByIdMap';
import { SCROLLABLE_QUESTION_CONTAINER_ID } from '~/components/Test/const';
import useReport from '~/hooks/useReport';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { VIDEO_PAGE_EVENTS } from '~/pages/SingleVideoPage/const';
import { ALPHABET, QUESTION_TYPES } from '~/pages/const';
import { CssUtilityType } from '~/styled';
import { MIDDLE_MARGIN_PX, SMALL_MARGIN_PX, SUBMIDDLE_MARGIN_PX } from '~/theme';
import { Maybe, QuizQuestion, QuizQuestionRadioType } from '~/types';
import getRandomIndex from '~/util/math/getRandomIndex';
import { toggleInArray } from '~/utils/arrayUtils/toggleInArray';
import sendAnalyticEvent from '~/utils/sendAnalyticEvent';

export interface QuestionFooterProps {
  question: QuizQuestion;
  goToExplanation?: () => void;
  isLastQuestion: boolean;
}

export interface QuestionHeaderProps {
  openReport: () => void;
  slidesGroupIndex?: number;
  infoLabel?: Maybe<string>;
}

interface IQuestionQuiz {
  renderNavigation?: ReactNode;
  isCorrectByIdMap: ReturnType<typeof createCorrectAnswersByIdMap>;
  selectedAnswersIdsByQuestionIdMap: { [key: number]: string[] };
  collectAnswersIdsByQuestionId: (
    questionId: QuizQuestion['id'],
    getIds: (oldIds: string[]) => string[],
  ) => void;
  contentWrapperCss?: CssUtilityType;
  questions: QuizQuestion[];
  className?: string;
  renderQuestionHeader: ({
    openReport,
    slidesGroupIndex,
    infoLabel,
  }: QuestionHeaderProps) => ReactNode;
  renderQuestionFooter: ({
    question,
    goToExplanation,
    isLastQuestion,
  }: QuestionFooterProps) => ReactNode;
  continueLearning?: () => void;
  selectedSlideIndex?: number;
  setSelectedSlideIndex?: Dispatch<SetStateAction<number>>;
  submittedQuestionsIds?: string[];
  hideArrowBackButton?: boolean;
  quizAnalyticsEvents?: typeof VIDEO_PAGE_EVENTS;
  controlledReport: ReturnType<typeof useReport>;
  colorsMode?: QuizColorsMode;
}

enum QuestionSlidesIndexes {
  Question,
  Report,
  Explanation,
}

export const SLIDES_PER_GROUP = 3;

const QuestionQuiz: FC<IQuestionQuiz> = ({
  submittedQuestionsIds = [],
  renderNavigation,
  isCorrectByIdMap,
  selectedAnswersIdsByQuestionIdMap,
  collectAnswersIdsByQuestionId,
  contentWrapperCss,
  questions,
  className,
  renderQuestionHeader,
  renderQuestionFooter,
  continueLearning,
  selectedSlideIndex,
  setSelectedSlideIndex,
  hideArrowBackButton = false,
  quizAnalyticsEvents,
  controlledReport,
  colorsMode,
}) => {
  const { t } = useTenantTranslation();
  const answersByQuestionId = useMemo(
    () =>
      Object.fromEntries(
        questions.map((question) => [question.id, [...(question?.answers || [])]]),
      ),
    [questions],
  );

  const viewTypesBuQuestionId = useMemo(
    () =>
      Object.fromEntries(
        questions.map((question) => {
          const radioTypeIndex = question?.radio_types?.length
            ? getRandomIndex(question.radio_types.length)
            : 0;
          const viewType =
            question?.frontend_type === 'radio' ? question?.radio_types?.[radioTypeIndex] : null;

          return [question.id, viewType];
        }),
      ),
    [questions],
  );

  // for each question we have to create 3 slides: quiz, report, explanation
  // so questionGroups array will look like: quiz1, report1, explanation1, quiz2, report2, explanation2...
  const questionGroups = questions.flatMap((question, slidesGroupIndex) => {
    const title = question?.title || '';
    const image = question?.image;
    const questionId = question?.id;
    const type = question?.frontend_type;
    const viewType = viewTypesBuQuestionId[questionId];
    const explanation = question?.description;
    const isLastQuestion = slidesGroupIndex === questions.length - 1;
    const answers = answersByQuestionId[question.id];
    const questionSubmitted = submittedQuestionsIds.includes(questionId);

    const orderAnswers = (answerIds: string[]) => {
      collectAnswersIdsByQuestionId(questionId, () => answerIds);

      quizAnalyticsEvents && sendAnalyticEvent(quizAnalyticsEvents.selectAnswer);
    };

    const selectCheckboxAnswer = (answerId?: Maybe<string>) =>
      collectAnswersIdsByQuestionId(questionId, (wasSelected = []) =>
        toggleInArray(answerId || '', wasSelected),
      );
    const selectRadioAnswer = (answerId?: Maybe<string>) =>
      collectAnswersIdsByQuestionId(questionId, () => [answerId || ''].filter(Boolean));

    // checkbox and radio answers
    const selectSingleAnswer = (answerId?: Maybe<string>) => {
      if (questionSubmitted) {
        return;
      }

      if (QUESTION_TYPES.check === type) {
        selectCheckboxAnswer(answerId);
      }

      if (QUESTION_TYPES.radio === type) {
        selectRadioAnswer(answerId);
      }

      quizAnalyticsEvents && sendAnalyticEvent(quizAnalyticsEvents.selectAnswer);
    };

    // each question has 3 slides: quiz, report, explanation
    const questionSlideIndex = slidesGroupIndex * SLIDES_PER_GROUP;
    const reportSlideIndex = questionSlideIndex + QuestionSlidesIndexes.Report;
    const explanationSlideIndex = questionSlideIndex + QuestionSlidesIndexes.Explanation;

    const getInfoLabel = () => {
      if (!type) {
        return null;
      }

      if (type === QUESTION_TYPES.radio) {
        if (viewType === QuizQuestionRadioType.Chat) {
          return t('page.test.chat');
        }

        if (viewType === QuizQuestionRadioType.EnterAnswer) {
          const allowedLetters = Array.from(ALPHABET.slice(0, answers?.length));

          return t('page.test.typeIn', {
            letters: allowedLetters.slice(0, -1).join(', '),
            letter: allowedLetters.at(-1),
          });
        }

        if (viewType === QuizQuestionRadioType.Carousel) {
          return t('page.test.carousel');
        }
      }

      return t(`page.test.${type}`);
    };

    return [
      ({ goToSlide }: ISlidesActions) => {
        const handleOpenExplainer = !!explanation
          ? () => goToSlide(explanationSlideIndex)
          : undefined;

        return (
          <>
            {renderNavigation}
            <Content id={SCROLLABLE_QUESTION_CONTAINER_ID} optionalStyles={contentWrapperCss}>
              <Container>
                {renderQuestionHeader({
                  openReport: () => goToSlide(reportSlideIndex),
                  slidesGroupIndex,
                  infoLabel: getInfoLabel(),
                })}
                <StyledQuestion
                  title={question.title}
                  type={type}
                  viewType={viewType}
                  image={image}
                  answers={answers}
                  selectedAnswersIds={selectedAnswersIdsByQuestionIdMap[questionId] || []}
                  isCorrectByIdMap={isCorrectByIdMap}
                  onOrderAnswers={orderAnswers}
                  onSelectSingleAnswer={selectSingleAnswer}
                  openExplainer={handleOpenExplainer}
                  continueLearning={continueLearning}
                  submitted={questionSubmitted}
                  colorsMode={colorsMode}
                />
                {renderQuestionFooter({
                  question,
                  goToExplanation: handleOpenExplainer,
                  isLastQuestion,
                })}
              </Container>
            </Content>
          </>
        );
      },
      ({ goToSlide }: ISlidesActions) => (
        <SlideContent optionalStyles={contentWrapperCss}>
          <Container data-testid="container">
            <ReportForm
              hideArrowBackButton={hideArrowBackButton}
              relatableEntityId={questionId}
              onCloseForm={() => goToSlide(questionSlideIndex)}
              controlledReport={controlledReport}
              colorsMode={colorsMode}
            />
          </Container>
        </SlideContent>
      ),
      ({ goToSlide }: ISlidesActions) => (
        <SlideContent optionalStyles={contentWrapperCss}>
          <Container>
            <QuestionExplainer
              title={<QuestionTitle title={title} colorsMode={colorsMode} />}
              explanation={explanation}
              goBack={() => goToSlide(questionSlideIndex)}
              hideArrowBackButton={hideArrowBackButton}
              colorsMode={colorsMode}
            />
          </Container>
        </SlideContent>
      ),
    ];
  });

  return (
    <StyledSlides
      className={className}
      selectedSlideIndex={selectedSlideIndex}
      setSelectedSlideIndex={setSelectedSlideIndex}
    >
      {questionGroups}
    </StyledSlides>
  );
};

export default QuestionQuiz;

const Content = styled('div', {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})<{ optionalStyles?: CssUtilityType }>`
  width: calc(100% - 48px);
  margin: 0 ${SUBMIDDLE_MARGIN_PX} ${MIDDLE_MARGIN_PX};
  overflow-y: auto;
  padding: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    padding: 0 ${SUBMIDDLE_MARGIN_PX} ${MIDDLE_MARGIN_PX};
    margin-top: ${SUBMIDDLE_MARGIN_PX};
  }

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.palette.common.card.overlay.contrast};
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 2px;
    background: ${({ theme }) => alpha(theme.palette.common.blue, 0.33)};
  }

  && {
    ${({ optionalStyles }) => optionalStyles}
  }
`;
const SlideContent = styled(Content)`
  height: 100%;
  margin-top: ${SMALL_MARGIN_PX};
`;
const Container = styled('div', {
  shouldForwardProp: (prop) => prop !== 'wrapperWidth',
})`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex-grow: 1;
  max-width: 800px;
  width: 100%;
  min-height: 100%;
  margin: 0 auto;
`;
const StyledSlides = styled(Slides)`
  width: 100%;
  overflow-x: hidden;
`;
const StyledQuestion = styled(Question)`
  margin-bottom: ${SUBMIDDLE_MARGIN_PX};
`;
